import React from 'react';
import { CircularProgress, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputLabel from '@mui/material/InputLabel';

const SelectStyled = styled(Select)(({ theme, disabled, error }) => ({
    height: '50px',
    borderRadius: '8px',

    '& .MuiInputBase-input': {
        color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.border.light,
        borderRadius: '10px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: error ? theme.palette.error.main : theme.palette.border.main,
        borderRadius: '10px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px',
        borderColor: disabled ? theme.palette.action.disabled : error ? theme.palette.error.main : theme.palette.border.light,
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.action.disabled,
    },
    '&.Mui-disabled': {
        cursor: 'not-allowed',
    },
}));

// Updated Icon component to handle disabled state
const Icon = styled(ExpandMoreIcon)<{ disabled?: boolean }>(({ theme, disabled }) => ({
    fill: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    color: '#000',
    '&:hover': {
        backgroundColor: theme.palette.primary.contrastText,
        color: '#fff',
    },
    '&.Mui-selected': {
        backgroundColor: theme.palette.primary.light,
    },
    '&&.Mui-selected:hover, &&.Mui-selected.Mui-focusVisible': {
        backgroundColor: theme.palette.primary.light,
    },
}));

interface MultiSelectComponentProps {
    value: string | null;
    onChange: (value: any) => void;
    options: string[];
    disabled: boolean;
    label: string;
    isLoading?: boolean;
    noOptions?: string;
    error?: boolean;
}

const MultiSelectComponent: React.FC<MultiSelectComponentProps> = (props) => {
    const { t } = useTranslation();

    return (
        <FormControl fullWidth>
            <InputLabel sx={{ color: props.disabled ? '#33333380' : '#333333' }} id={'demo-select-large-label'}>
                {props.label}
            </InputLabel>
            <SelectStyled
                labelId={'demo-select-large-label'}
                id={'demo-select-small'}
                value={props.value ?? ''}
                disabled={props.disabled}
                label={props.label}
                onChange={(event: any) => props.onChange(event.target.value)}
                IconComponent={(iconProps) => <Icon {...iconProps} disabled={props.disabled} />}
                renderValue={(selected) => {
                    if (props.isLoading) {
                        return <CircularProgress size={20} />;
                    }
                    return selected ? (selected as string) : <em>{props.label}</em>;
                }}
                error={props.error}
            >
                {props.isLoading ? (
                    <MenuItem disabled>
                        <CircularProgress size={20} />
                        <Typography sx={{ ml: 2 }}>{t('common.loading')}</Typography>
                    </MenuItem>
                ) : props.options.length === 0 ? (
                    <MenuItem disabled>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {props.noOptions || 'No options available'}
                        </Typography>
                    </MenuItem>
                ) : (
                    props.options.map((option) => (
                        <MenuItemStyled key={option} value={option}>
                            <Typography variant={'body2'}>{option}</Typography>
                        </MenuItemStyled>
                    ))
                )}
            </SelectStyled>
        </FormControl>
    );
};

export default MultiSelectComponent;
